import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { TimelinePoint } from '@/lib/types';

interface TimelineChartProps {
  data: TimelinePoint[];
}

const chartConfig = {
  clicks: { label: 'Clicks', color: 'var(--chart-1)' },
  landingClicks: { label: 'Landing clicks', color: 'var(--chart-2)' },
  installs: { label: 'Installs', color: 'var(--chart-3)' },
  signups: { label: 'Signups', color: 'var(--chart-4)' },
} satisfies ChartConfig;

const SERIES = Object.keys(chartConfig) as Array<keyof typeof chartConfig>;

function formatDay(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function TimelineChart({ data }: TimelineChartProps) {
  return (
    <ChartContainer
      config={chartConfig}
      className="aspect-auto h-[280px] w-full"
    >
      <AreaChart
        accessibilityLayer
        data={data}
        margin={{ left: 12, right: 12 }}
      >
        <defs>
          {SERIES.map((key) => (
            <linearGradient key={key} id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={`var(--color-${key})`} stopOpacity={0.8} />
              <stop offset="95%" stopColor={`var(--color-${key})`} stopOpacity={0.1} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="day"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={formatDay}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              indicator="dot"
              labelFormatter={(value) => formatDay(String(value))}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        {SERIES.map((key) => (
          <Area
            key={key}
            dataKey={key}
            type="natural"
            fill={`url(#fill-${key})`}
            fillOpacity={0.4}
            stroke={`var(--color-${key})`}
          />
        ))}
      </AreaChart>
    </ChartContainer>
  );
}
